import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: {
        padding: "0 8px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: "6px",
        height: "28px",
    },
    sm: {
        padding: "0 10px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: "6px",
        height: "32px",
    },
    md: {
        padding: "0 10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: "7px",
        height: "36px",
    },
    lg: {
        padding: "0 10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: "7px",
        height: "40px",
    },
};

const defaultFocusVisibleState = {
    outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
    boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
};

const errorState = {
    outline: `1px solid ${tokens.colors.ui.destructive}`,
    boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const StyledInputContainer = styled.div(({ size = "lg" }) => {
    const { padding, fontSize, lineHeight, borderRadius, height } = sizes[size];

    return {
        display: "inline-flex",
        alignItems: "center",
        width: "100%",
        boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
        transition: "all 0.2s ease",
        gap: "8px",
        padding,
        fontSize,
        lineHeight,
        borderRadius,
        height,
        background: tokens.colors.ui.background,
        border: `1px solid ${tokens.colors.ui.border}`,
        color: tokens.colors.ui.foreground,

        "&:hover:not(.disabled)": {
            borderColor: tokens.colors.brand.blue["500"],
        },

        "&:focus-within:not(.disabled)": defaultFocusVisibleState,

        '&[data-error="true"]': errorState,

        "&:disabled, &.disabled": {
            opacity: 0.5,
            cursor: "not-allowed",
            background: tokens.colors.brand.blue["100"],
        },

        "&:focus-within": {
            outline: "none",
        },

        "& .input-field": {
            flex: 1,
            minWidth: 0,
            width: "100%",
            padding: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            color: tokens.colors.ui.foreground,
            fontFamily: "inherit",
            fontSize: "inherit",
            fontWeight: tokens.typography.fontWeights.regular,
            lineHeight: "inherit",

            "&::placeholder": {
                color: tokens.colors.ui["muted-foreground"],
                fontWeight: tokens.typography.fontWeights.regular,
            },

            "&:disabled": {
                cursor: "not-allowed",
            },
        },

        "& .right-icon": {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: tokens.colors.ui.foreground,
            fontSize: "14px",
            lineHeight: 1,
            pointerEvents: "none",

            "& i, & svg": {
                display: "block",
                width: "1em",
                height: "1em",
            },
        },
    };
});

const StyledFieldWrapper = styled.div(() => ({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
}));

const StyledLabel = styled.label(() => ({
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "20px",
    color: tokens.colors.ui.foreground,
    display: "flex",
    alignItems: "center",
    gap: "4px",

    '&[data-required="true"]::after': {
        content: '"*"',
        color: tokens.colors.ui.destructive,
        marginLeft: "2px",
    },
}));

const StyledErrorText = styled.span(() => ({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "16px",
    color: tokens.colors.ui.destructive,
    minHeight: "1.4em",
}));

const StyledHelperText = styled.span(() => ({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "16px",
    color: tokens.colors.ui["muted-foreground"],
}));

/**
 * A text input component with label, error, helper text, and optional right icon.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.placeholder="Type here"] - Placeholder text for the input
 * @param {string} [props.value] - Controlled value of the input
 * @param {function} [props.onChange] - Callback fired when value changes
 * @param {string} [props.size="lg"] - Size variant: "xs", "sm", "md", or "lg"
 * @param {boolean} [props.disabled=false] - If true, the input is disabled
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {string} [props.label] - Label text for the input
 * @param {string} [props.helperText] - Helper text to display below the input
 * @param {string} [props.errorText] - Error message to display
 * @param {React.ReactNode} [props.iconRight] - Icon to display on the right side
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @returns {JSX.Element} The rendered text input component
 */
const TextInput = forwardRef(
    (
        {
            placeholder = "Type here",
            value,
            onChange,
            size = "lg",
            disabled = false,
            error = false,
            label,
            helperText,
            errorText,
            iconRight,
            ...props
        },
        ref
    ) => {
        const showError = Boolean(errorText || error);

        return (
            <StyledFieldWrapper>
                {label && (
                    <StyledLabel
                        htmlFor={props.id}
                        data-required={props.required ? "true" : undefined}
                    >
                        {label}
                    </StyledLabel>
                )}

                <StyledInputContainer
                    size={size}
                    className={disabled ? "disabled" : ""}
                    data-error={showError ? "true" : undefined}
                >
                    <input
                        ref={ref}
                        type="text"
                        className="input-field"
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        placeholder={placeholder}
                        aria-invalid={showError || undefined}
                        {...props}
                    />

                    {iconRight && (
                        <span className="right-icon" aria-hidden="true">
                            {iconRight}
                        </span>
                    )}
                </StyledInputContainer>

                {errorText ? (
                    <StyledErrorText>{errorText}</StyledErrorText>
                ) : helperText ? (
                    <StyledHelperText>{helperText}</StyledHelperText>
                ) : null}
            </StyledFieldWrapper>
        );
    }
);

TextInput.displayName = "TextInput";

export default TextInput;